import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Notiflix from 'notiflix';
import { useTranslation } from 'react-i18next';

const ContactModal = ({ isOpen, onClose }) => {
    const { t } = useTranslation();
    
    // State Management
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [message, setMessage] = useState('');
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Formspree Endpoint
    const FORMSPREE_URL = "https://formspree.io/f/mrebwnbp";

    const clearErrors = () => setErrors({});

    const clearInput = () => {
        setFirstName('');
        setLastName('');
        setEmail('');
        setPhone('');
        setMessage('');
        setErrors({});
    };

    const sendEmail = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setErrors({});

        Notiflix.Report.init({
            success: {
                svgColor: '#2563eb',
                buttonBackground: '#2563eb',
                titleColor: '#1e293b',
                messageColor: '#475569',
            },
        });

        const formData = {
            first_name: firstName,
            last_name: lastName,
            email: email,
            phone_number: phone,
            message: message,
        };

        try {
            const response = await axios.post(FORMSPREE_URL, formData, {
                headers: { 'Accept': 'application/json' }
            });

            if (response.status === 200) {
                setIsSubmitting(false);
                clearInput();
                Notiflix.Report.success(
                    t('contact.notices.success_title'),
                    t('contact.notices.success_msg'),
                    t('contact.notices.btn_ok'),
                );
                onClose();
            }
        } catch (error) {
            setIsSubmitting(false);
            const { response } = error;

            if (response && response.data && response.data.errors) {
                const mappedErrors = {};
                response.data.errors.forEach(err => {
                    mappedErrors[err.field] = err.message;
                });
                setErrors(mappedErrors);
                Notiflix.Notify.failure(t('contact.notices.validation_fail'));
            } else {
                Notiflix.Report.failure(
                    t('contact.notices.error_title'),
                    t('contact.notices.error_msg'),
                    t('contact.notices.btn_ok'),
                );
            }
        }
    };

    useEffect(() => {
        const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
            <div 
                className="relative w-full max-w-5xl max-h-[90vh] overflow-y-auto bg-white rounded-3xl shadow-2xl animate-slideUp"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-6 right-6 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-red-500 hover:text-white transition-all duration-300 group"
                >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>

                <div className="grid lg:grid-cols-2 gap-0">
                    {/* Left Side */}
                    <div className="bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 p-8 lg:p-12 rounded-l-3xl text-white">
                        <h2 className="text-3xl lg:text-4xl font-bold mb-4">{t('contact.left_title')}</h2>
                        <p className="text-blue-200 mb-8 text-lg">{t('contact.left_subtitle')}</p>

                        <div className="space-y-6">
                            <div className="flex gap-4">
                                <div className="flex-shrink-0 w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="font-semibold text-lg mb-1">{t('contact.office_title')}</h3>
                                    <div className="text-blue-200 space-y-2">
                                        <p><span className="font-semibold text-white">Canada:</span><br />2151 Rue Léger Lasalle, Montréal, Québec H8N 0J8</p>
                                        <p><span className="font-semibold text-white">Nigeria:</span><br />39 Alternative Road, Lekki Chevron</p>
                                    </div>
                                </div>
                            </div>

                            <div className="flex gap-4">
                                <div className="flex-shrink-0 w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                                </div>
                                <div>
                                    <h3 className="font-semibold text-lg mb-1">{t('contact.call_title')}</h3>
                                    <div className="text-blue-200 space-y-1">
                                        <p><span className="font-semibold text-white">Canada:</span> +1 (514) 210-0201</p>
                                        <p><span className="font-semibold text-white">Nigeria:</span> +234 070 5037 5944</p>
                                    </div>
                                </div>
                            </div>

                            <div className="flex gap-4">
                                <div className="flex-shrink-0 w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                                </div>
                                <div>
                                    <h3 className="font-semibold text-lg mb-1">{t('contact.email_title')}</h3>
                                    <p className="text-blue-200">info@lakche.com</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Side */}
                    <div className="p-8 lg:p-12">
                        <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2">{t('contact.right_title')}</h3>
                        <p className="text-gray-600 mb-6">{t('contact.right_subtitle')}</p>

                        <form onSubmit={sendEmail} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">{t('contact.form.first_name')}</label>
                                    <input
                                        className={`w-full bg-gray-50 text-gray-900 px-4 py-3 rounded-lg border ${errors.first_name ? 'border-red-500' : 'border-gray-200'} focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all`}
                                        type="text"
                                        placeholder={t('contact.form.placeholder_first')}
                                        value={firstName}
                                        onChange={(e) => {setFirstName(e.target.value); clearErrors();}}
                                    />
                                    {errors.first_name && <p className="text-red-500 text-xs mt-1">{errors.first_name}</p>}
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">{t('contact.form.last_name')}</label>
                                    <input
                                        className={`w-full bg-gray-50 text-gray-900 px-4 py-3 rounded-lg border ${errors.last_name ? 'border-red-500' : 'border-gray-200'} focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all`}
                                        type="text"
                                        placeholder={t('contact.form.placeholder_last')}
                                        value={lastName}
                                        onChange={(e) => {setLastName(e.target.value); clearErrors();}}
                                    />
                                    {errors.last_name && <p className="text-red-500 text-xs mt-1">{errors.last_name}</p>}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">{t('contact.form.email')}</label>
                                    <input
                                        className={`w-full bg-gray-50 text-gray-900 px-4 py-3 rounded-lg border ${errors.email ? 'border-red-500' : 'border-gray-200'} focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all`}
                                        type="email"
                                        placeholder="john@example.com"
                                        value={email}
                                        onChange={(e) => {setEmail(e.target.value); clearErrors();}}
                                    />
                                    {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">{t('contact.form.phone')}</label>
                                    <input
                                        className={`w-full bg-gray-50 text-gray-900 px-4 py-3 rounded-lg border ${errors.phone_number ? 'border-red-500' : 'border-gray-200'} focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all`}
                                        type="tel"
                                        placeholder="+1 (514) 000-0000"
                                        value={phone}
                                        onChange={(e) => {setPhone(e.target.value); clearErrors();}}
                                    />
                                    {errors.phone_number && <p className="text-red-500 text-xs mt-1">{errors.phone_number}</p>}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">{t('contact.form.message')}</label>
                                <textarea
                                    className={`w-full h-32 bg-gray-50 text-gray-900 px-4 py-3 rounded-lg border ${errors.message ? 'border-red-500' : 'border-gray-200'} focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none`}
                                    placeholder={t('contact.form.placeholder_msg')}
                                    value={message}
                                    onChange={(e) => {setMessage(e.target.value); clearErrors();}}
                                ></textarea>
                                {errors.message && <p className="text-red-500 text-xs mt-1">{errors.message}</p>}
                            </div>

                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold py-4 px-6 rounded-lg transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                            >
                                {isSubmitting ? t('contact.form.btn_sending') : t('contact.form.btn_send')}
                            </button>
                        </form>
                    </div>
                </div>
            </div>

            <style jsx>{`
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
                .animate-fadeIn { animation: fadeIn 0.2s ease-out; }
                .animate-slideUp { animation: slideUp 0.3s ease-out; }
            `}</style>
        </div>
    );
};

export default ContactModal;